import React, { Fragment, useMemo, useContext, useEffect, useState } from 'react';
import { CartContext } from 'components/cart';
import classNames from 'lib/classNames';
import HeaderMachine from '../machines/HeaderMachine';
import { useMachine } from '@xstate/react';
import useRefState from 'hooks/useRefState';
import useCampaign from 'hooks/useCampaign';
import promisify from 'lib/promisify';
import { getAllOCUProducts } from './CheckoutButton';
import Axios from 'axios';
import { FilterAtuGoals } from '../Utils';
import GoalImageContent from './GoalImages';

export default function HeaderATU({ campaign_id, add_to_unlock, bundlesDiscounts }) {
    const {
        cart_settings: {
            theme: { primary, background },
            font: { color },
            rtl,
            money_format
        },
        campaigns,
        setShowConfetti,
        shopify_cart_items,
        Utils,
        add_to_cart,
        update_cart_item,
        get_total,
        productNotAddedToCart,
        onlyOneItemAddedToCart,
        productAddedToCart,
        discountCode,
        setCartItems,
        AppLocalStorage,
        hasAutoDiscount,
        shipProtectState
    } = useContext(CartContext);
    const [__, setContextCache, contextCache] = useRefState(null);
    const [one_check_up_sell_product_id, ocu_campaign] = useCampaign(
        'one_check_up_sell',
        null,
        true
    )(campaigns, shopify_cart_items);
    const ocu_products = getAllOCUProducts(ocu_campaign);
    const filteredItems = shopify_cart_items.filter((item) => {
        const ocu_product = ocu_products.find(
            ({ product }) => item.product_id.toString() === product?.id
        );
        return ocu_product
            ? ocu_product.include_in_atu
            : !(['free_gift', 'singleton_gift'].includes(item?.product_type?.toLowerCase()) && item?.price === 0)
    });
    const { original, subtotal, autoDiscountGifts } = get_total(null, filteredItems);
    const byQuantity = add_to_unlock.multi_goal_type === 'quantity';
    const headerMachine = useMemo(
        () =>
            HeaderMachine({
                campaign_id,
                goals: FilterAtuGoals(
                    add_to_unlock.goals,
                    !!discountCode?.value || hasAutoDiscount
                ),
                show_badges:add_to_unlock.show_badges,
                setShowConfetti,
                shopify_cart_items: filteredItems,
                original,
                autoDiscountGifts,
                subtotal,
                bundlesDiscounts,
                discountCode,
                byQuantity,
                contextCache: contextCache.current
            }),
        [
            shopify_cart_items,
            add_to_unlock,
            bundlesDiscounts,
            contextCache.current,
            hasAutoDiscount,
            discountCode,
            shipProtectState
        ]
    );
    // const [current, send, service] = useMachine(headerMachine);
    const { context } = headerMachine;
const hasImage = context.goals.filter(({ goalObject: { badge } }) => add_to_unlock.show_badges).length > 0;
    useEffect(() => {
        const giftGoals = context.goals.filter(
            ({ goalObject }) => goalObject.reward_type === 'gift'
        );
        if (giftGoals.length) {
            handleGifts({
                giftGoals,
                AppLocalStorage,
                Utils,
                discountCode,
                productNotAddedToCart,
                onlyOneItemAddedToCart,
                productAddedToCart,
                setCartItems
            });
        }
    }, [discountCode, shopify_cart_items]);

    useEffect(() => {
        if (JSON.stringify(context) !== JSON.stringify(contextCache.current)) {
            if (contextCache.current) {
                const showConfettiPreview = context?.goals?.filter(
                    ({ goalObject: { confetti } }, i) =>
                        confetti && !contextCache?.current?.goals[i]?.goalObject?.confetti
                );
                if (showConfettiPreview.length) {
                    setShowConfetti(showConfettiPreview[0].goalObject.confetti_length);
                }
            }
            setContextCache(context);
        }
    }, [context]);

    const hasLongBadgePrice = context?.goals?.filter(({goalObject}) => {
        const {badge,goal} = goalObject
        if(!badge) return false;
        const formattedGoal = badge?.text?.text.replace('{{goal}}', add_to_unlock.multi_goal_type === 'quantity' ? goal : Utils.formatMoney(goal * 100,money_format));
        const matches = formattedGoal.match(/>(.*?)<|^[^<>]+$/);
        const extractedText =
          matches && matches.length > 1 && matches[1]
            ? matches[1]
            : matches && matches.length > 0
            ? matches[0]
            : "";

        return extractedText
        ? extractedText.length > 8 && extractedText.split(" ").length > 1
        : false;
    }).length
    
    return (
        <div
            className={classNames('atu-banner px-5 pb-2 mb-px border-b border-gray-400')}
            data-offset={1}
            style={{
                background: add_to_unlock.progress_background
                    ? Utils.formatColor(add_to_unlock.progress_background)
                    : 'inherit',
                paddingBottom: hasImage ? hasLongBadgePrice ? '43px' : "30px" : undefined,
            }}
            data-quantity={`${filteredItems.map((item) => item.quantity).reduce((acc, item) => acc + item, 0)}`}
            // data-subtotal={`${subtotal}`}
        >
            <div className={classNames('goal-text w-full')} 
            style={{
                marginBottom: hasImage ? '20px': undefined,
            }}
            >
                {context.goals.map(
                    ({ textKey, goalObject, unlocked, previousIsUnlocked, isFirst, isLast }, index) => (
                        <GoalText
                            key={textKey}
                            unlocked={unlocked}
                            bundlesDiscounts={bundlesDiscounts}
                            currentGoal={goalObject}
                            add_to_unlock={add_to_unlock}
                            filteredItems={filteredItems}
                            otherUnlockedDiscounts={context.goals.filter(
                                ({ unlocked, goalObject: { reward_type } }, i) =>
                                    i !== index && unlocked && reward_type === 'discount'
                            )}
                            previousIsUnlocked={previousIsUnlocked}
                            show_all_locked_goals={add_to_unlock.show_all_locked_goals}
                            {...{ isFirst, isLast }}
                        />
                    )
                )}
            </div>
            <div
                className={classNames(
                    'progress-bar-container flex w-full relative mix-blend-darken',
                    add_to_unlock.progress_thick ? 'h-4 rounded-4' : 'h-2 rounded-2'
                )}
                style={{
                    background: Utils.formatColor(add_to_unlock.progress_secondary),
                }}
            >
                <div
                    className={classNames(
                        'progress-bar block transition-width absolute inset-0 duration-500 ease-out z-10',
                        add_to_unlock.progress_thick ? 'h-4 rounded-4' : 'h-2 rounded-2'
                    )}
                    style={{
                        background:
                            context.goals.filter(({ unlocked }) => !unlocked).length > 0
                                ? Utils.formatColor(add_to_unlock.progress_primary)
                                : Utils.formatColor(add_to_unlock.progress_complete),
                        width: `${getProgressWidth(context.goals, byQuantity, filteredItems, original, subtotal)}%`
                    }}
                />
                {context.goals.map(({ key,goalObject, isFirst, isLast,unlocked }, index) => (
                    <ProgressBar
                        key={key}
                        add_to_unlock={add_to_unlock}
                        unlocked={unlocked}
                        goal={goalObject}
                        goals={context.goals}
                        isFirst={isFirst}
                        isLast={isLast}
                        borderColor="#718096"
                    />
                ))}
            </div>
            {/* {context.goals.length > 1 && ( */}
            <div className={classNames('goals flex justify-between text-gray-600 relative h-3')}>
                {context.goals.map(
                    ({ labelKey, goalObject, isLast }, index) => {
                        const { goal_text,badge,reward_type, show_goal_text } = goalObject;
                        if (!show_goal_text || !!add_to_unlock.show_badges) return null;
                        return (
                            <div
                                key={labelKey}
                                className={classNames('goal absolute transform w-max text-center')}
                                data-goal={Utils.formatMoney(goalObject.goal * 100 * (parseFloat(window.Shopify.currency.rate) || 1), money_format)}
                                style={{
                                    left: !rtl && `${((index + 1) / context.goals.length) * 100}%`,
                                    right: rtl && `${((index + 1) / context.goals.length) * 100}%`,
                                    width: hasImage ? `${100 / context.goals.length}%` : 'fit-content',
                                    whiteSpace: 'nowrap',
                                    fontSize: Utils.em(goal_text.font_size),
                                    color: Utils.formatColor(goal_text.color),
                                    '--transform-translate-x': hasImage ? `${100 * (rtl ? 1 : -1)}%` : `${
                                        (isLast ? 100 : 50) * (rtl ? 1 : -1)
                                    }%`
                                }}
                            >
                                {goal_text.text}
                            </div>
                        );
                    }
                )}
            </div>
            {/* )} */}
        </div>
    );
}

const ProgressBar = ({ isFirst, isLast, borderColor, goal,goals, unlocked,add_to_unlock }) => {
    const {
        cart_settings: {
            theme: { background },
            rtl,
            money_format
        },
        Utils
    } = useContext(CartContext);
    const {reward_type, badge } = goal;
    const formattedGoal = badge?.text?.text.replace('{{goal}}', add_to_unlock.multi_goal_type === 'quantity' ? goal.goal : Utils.formatMoney(goal.goal * 100,money_format));
    const hasUploadedImage = badge?.type === 1 && badge?.image
    const roundedFirst = add_to_unlock.progress_thick ? rtl ? 'rounded-r-4' : 'rounded-l-4' :  rtl ? 'rounded-r-2' : 'rounded-l-2';
    const roundedLast = add_to_unlock.progress_thick ? rtl ? 'rounded-l-4' : 'rounded-r-4' :  rtl ? 'rounded-l-2' : 'rounded-r-2';
    const goalsLength = goals.length
    const direction = (size) => ({[rtl? 'right' : 'left']: size})
    // right-1/2
    // left-1/2
    // right-4
    // left-4
    const completed = goals.filter(({ unlocked }) => !unlocked).length === 0
    
    return (
        <div
            className={classNames('badge-goal relative flex-1 bg-transparent z-20 mix-blend-overlay', 
            add_to_unlock.progress_thick ? 'h-4' : 'h-2',
            {
                [roundedFirst]: isFirst,
                [roundedLast]: isLast,
                'border-r': !add_to_unlock.show_badges && !rtl && !isLast,
                'border-l': !add_to_unlock.show_badges && rtl && !isLast
            })}
            style={{ borderColor, display: 'block' }}
        >
            {!!add_to_unlock.show_badges && badge && <div 
                        className={classNames('z-20 w-55px h-55px rounded-full border-3 absolute top-1/2 transform text-12px font-bold leading-tight')} 
                        style={{
                            ...direction(goalsLength > 2 ? '50%' : goalsLength === 1 ? '80%' : isFirst ? '85%' : '70%'),
                            background: Utils.formatColor(background),
                            color: Utils.formatColor(add_to_unlock.progress_primary),
                            '--transform-translate-y': '-50%',
                            '--transform-translate-x': !(goalsLength === 2 && isFirst) ? '-50%' : 0,
                            borderColor: Utils.formatColor(unlocked ? completed ? add_to_unlock.progress_complete : add_to_unlock.progress_primary : add_to_unlock.progress_secondary),
                        }}>
                            <div style={{
                                backgroundImage: hasUploadedImage ? `url(${badge?.image})` : undefined
                            }} className={classNames('relative bg-cover bg-center flex items-center justify-center flex-col w-full h-full rounded-full')}>
                                {!hasUploadedImage && <GoalImageContent goal={goal} />}
                                <span className={classNames('absolute w-full text-center', {
                                    hidden: badge.show_text !== true
                                })} style={{top: '53px', 
                                    fontSize: Utils.em(badge?.text?.font_size),
                                    color: Utils.formatColor(badge?.text?.color),
                            }} dangerouslySetInnerHTML={{__html:formattedGoal}}/>
                            </div>
                    </div>
            }
        </div>
    );
};

const textIsEmpty = (html) => {
    const element = document.createElement('span');
    element.innerHTML = html;
    return element.innerText.length === 0;
};

const GoalText = ({
    currentGoal,
    add_to_unlock: { multi_goal_type },
    unlocked,
    otherUnlockedDiscounts,
    previousIsUnlocked,
    show_all_locked_goals,
    bundlesDiscounts,
    filteredItems,
    isLast,
    isFirst
}) => {
    const {
        cart_settings: {
            font: { color },
            money_format
        },
        Utils,
        get_total,
        discountCode
    } = useContext(CartContext);
    const { goal, reward_type } = currentGoal;
    const reward = currentGoal[reward_type];
    const isDiscount = reward_type === 'discount';
    const notTheHighestDiscount = isDiscount
        ? !!otherUnlockedDiscounts.find(
              ({ goalObject: { discount } }) => discount.reward > reward.reward
          )
        : false;
    const text = unlocked ? reward.after : reward.before;
    // if (
    //   textIsEmpty(text) ||
    //   notTheHighestDiscount ||
    //   previousIsUnlocked === false ||
    //   (!isLast && !show_all_locked_goals)
    // )
    //   return null;
    if (
        !textIsEmpty(text) &&
        !notTheHighestDiscount &&
        ((show_all_locked_goals && unlocked) ||
            (isFirst && !unlocked) ||
            (isLast && unlocked) ||
            (!unlocked && previousIsUnlocked))
    )
        //   if(!textIsEmpty(text) && !notTheHighestDiscount &&
        //   ((isFirst && ((unlocked && show_all_locked_goals) || !unlocked) ) ||
        //   (isLast && (unlocked || (!unlocked && previousIsUnlocked)))) ||
        //   (!isLast && !isFirst && ((unlocked && show_all_locked_goals) || (!unlocked && previousIsUnlocked)))
        // )
        return (
            <div
                className={classNames('emoji-image mb-0 text-center')}
                style={{ color }}
                dangerouslySetInnerHTML={{
                    __html: formateText(
                        text,
                        reward_type,
                        reward,
                        (v) => Utils.formatMoney(v, money_format),
                        get_total,
                        goal,
                        multi_goal_type,
                        filteredItems,
                        bundlesDiscounts,
                        discountCode
                    )
                }}
            ></div>
        );
    else return null;
};

const formateText = (
    text,
    rewardType,
    { type, reward },
    formatMoney,
    get_total,
    goal,
    multi_goal_type,
    filteredItems,
    bundlesDiscounts,
    discountCode
) => {
    const rate = Number(window.Shopify.currency.rate);
    let replacedReward =
      rewardType === "discount"
        ? type === "percentage"
          ? `${reward}%`
          : formatMoney(reward * rate * 100)
        : "";
    const formattedGoal = parseInt(goal * 100);
    const { subtotal, original } = get_total(null, filteredItems);
	const totalPrice = filteredItems.reduce((acc, cur) => {
		let price;
		if(cur.selling_plan_allocation && cur.selling_plan_allocation.compare_at_price) {
			price = cur.selling_plan_allocation.compare_at_price;
		} else {
			price = cur.price;
		}
		return acc + price * cur.quantity - cur.discounts?.reduce((acc, { amount }) => acc + amount, 0);
	}, 0);
    const currentAmount = rewardType === 'free_shipping' ? (window.mu_include_sub_full_price ? totalPrice : subtotal) : original - bundlesDiscounts;
    let replacedAmount = formatMoney(formattedGoal - currentAmount);
    let replacedQuantity =
        goal - filteredItems.map((item) => item.quantity).reduce((acc, item) => acc + item, 0);

    return text
        .replace(
            '{{amount_left}}',
            multi_goal_type === 'quantity' ? replacedQuantity : replacedAmount
        )
        .replace('{{reward}}', replacedReward)
        .replace('{{goal}}', formatMoney(goal * 100))
        .replace('https://www.amabattery.co.za/wpimages/wp4cb45f10.gif', 'https://monster-upsells-images-prod.s3.us-east-2.amazonaws.com/wp4cb45f10.gif')
};

function pickRandomGift({ AppLocalStorage, variants, key }) {
    const cachedVariant = AppLocalStorage.getItem(key);
    const generateVariant = () => {
        const variant = [...variants].sort(() => 0.5 - Math.random())[0];
        AppLocalStorage.setItem(
            key,
            JSON.stringify({
                cached_at: Date.now(),
                variant
            })
        );
        return variant;
    };
    if (cachedVariant) {
        const { cached_at, variant } = JSON.parse(cachedVariant);
        const eleven_hours = 12 * 60 * 60 * 100;
        if (Date.now() - cached_at > eleven_hours) {
            return generateVariant();
        }
        return variant;
    }
    return generateVariant();
}

function getVariants(products) {
    return products.map(({ variants }) => variants).flat();
}

function addedVariants(variants, productAddedToCart, type = 'find') {
    if (!['find', 'filter'].includes(type)) throw new Error('type must be find or filter');
    return variants[type]((variant) => productAddedToCart(variant, true));
}

async function handleGifts({
    giftGoals,
    AppLocalStorage,
    Utils,
    discountCode,
    productNotAddedToCart,
    onlyOneItemAddedToCart,
    productAddedToCart,
    setCartItems
}) {
    const { add, remove } = giftGoals.reduce(
        (acc, goal, index) => {
            const { campaign_id, key, goalObject } = goal;
            const { product, multi_gift_select, previous_gift } = goalObject.gift;
            const products =
                goalObject.gift.products ||
                [
                    goalObject.gift.product
                        ? { variants: [{ id: goalObject.gift.product.variant_id }] }
                        : null
                ].filter(Boolean);
            const previous_goal = previous_gift === 'remove' && index > 0 && giftGoals[index - 1];
            const next_goal = index < giftGoals.length - 1 && giftGoals[index + 1];
            const giftsCount = products?.length;
            const isRandom = multi_gift_select === 'random';
            const GiftMustBeAddedManually =
                !isRandom && (giftsCount === 1 ? !Utils.productHasOneVariant(products[0]) : true);
            const variants = getVariants(
                products ||
                    [product ? { variants: [{ id: product.variant_id }] } : null].filter(Boolean)
            );
            const oneVariantIsAdded = addedVariants(variants, productAddedToCart);
            const randomVariant =
                !oneVariantIsAdded &&
                pickRandomGift({
                    AppLocalStorage,
                    variants,
                    key: `multi_gifts_${campaign_id}_${key}`
                });
            const { origin } = window.location;
            variants.forEach((variant) => {
                const action = handleGiftProduct({
                    goal,
                    variant,
                    discountCode,
                    productNotAddedToCart,
                    onlyOneItemAddedToCart,
                    productAddedToCart
                });
                if (action) {
                    if (action === 'add' && products) {
                        if (previous_goal && !GiftMustBeAddedManually) {
                            const gift = previous_goal.goalObject.gift;
                            const previous_goal_products =
                                gift.products ||
                                [
                                    gift.product
                                        ? { variants: [{ id: gift.product.variant_id }] }
                                        : null
                                ].filter(Boolean);
                            const added_variants = addedVariants(
                                getVariants(previous_goal_products),
                                productAddedToCart,
                                'filter'
                            );
                            added_variants.forEach((variant) => {
                                acc['remove'].push({ id: variant.id });
                            });
                        }
                        if (
                            next_goal &&
                            next_goal.unlocked &&
                            next_goal.goalObject.gift.previous_gift === 'remove' &&
                            variants.find((v) => v.id === variant.id)
                        ) {
                            return;
                        }
                        if (
                            GiftMustBeAddedManually ||
                            (isRandom && variant.id !== randomVariant.id)
                        ) {
                            return;
                        }
                    }
                    acc[action].push({ id: variant.id });
                }
            });
            return acc;
        },
        { add: [], remove: [] }
    );
    if (add.length) {
        const updates = add.reduce((acc, { id }) => ({ ...acc, [id]: 1 }), {});
        const { data } = await Axios.post(Utils.getLangPath('cart/update.js'), { updates });
        if (data?.items) {
            setCartItems(data.items, data.cart_level_discount_applications);
        }
    }
    if (remove.length) {
        const updates = remove.reduce((acc, { id }) => ({ ...acc, [id]: 0 }), {});
        const { data } = await Axios.post(Utils.getLangPath('cart/update.js'), { updates });
        if (data?.items) {
            setCartItems(data.items.filter(({quantity}) => quantity !== 0), data.cart_level_discount_applications);
        }
    }
}
function handleGiftProduct({
    goal,
    variant,
    discountCode,
    productNotAddedToCart,
    onlyOneItemAddedToCart,
    productAddedToCart
}) {
    const { unlocked } = goal;
    if (!variant) return;
    // we're using !onlyOneItemAddedToCart(product) here, because add will force the item quantity to be 1
    if (
        unlocked &&
        (productNotAddedToCart(variant, true) || !onlyOneItemAddedToCart(variant, true))
    ) {
        return 'add';
    } else if (!unlocked && productAddedToCart(variant, true)) {
        return 'remove';
    }
    return null;
}

function getProgressWidth(goals, byQuantity, filteredItems, original, subtotal) {
    if(!window.mu_useNewProgressWidth) return (
      goals.reduce((acc, cur) => {
        const { unlocked, isFirst, previousIsUnlocked, value } = cur;
        return isFirst || previousIsUnlocked ? acc + value : acc;
      }, 0) / goals.length
    );
    if (goals.length === 0) return 0;
    
    // Get the current value
    const currentValue = byQuantity 
        ? filteredItems.reduce((acc, item) => acc + item.quantity, 0)
        : subtotal/100;
    
    // Get the maximum goal value
    const maxGoal = Math.max(...goals.map(goal => goal.goalObject.goal));
    
    // Early returns for edge cases
    if (currentValue >= maxGoal) return 100;
    if (!currentValue) return 5; // Minimum 5% progress for visual indication
    
    // Find the first incomplete goal index
    const firstIncompleteIndex = goals.findIndex(goal => !goal.unlocked);
    
    if (byQuantity) {
        // Calculate progress for quantity-based goals
        const completedProgress = goals.reduce((acc, cur, index) => {
            if (cur.unlocked) return acc + cur.value;
            if (index === firstIncompleteIndex) {
                // Calculate partial progress for the first incomplete goal
                const prevGoalValue = index > 0 ? goals[index - 1].goalObject.goal : 0;
                const progressRatio = (currentValue - prevGoalValue) / (cur.goalObject.goal - prevGoalValue);
                return acc + (progressRatio * cur.value);
            }
            return acc;
        }, 0);
        
        return Math.max(5, (completedProgress / maxGoal) * 100);
    } else {
        // Calculate progress for amount-based goals
        return Math.max(5, goals.reduce((acc, cur) => { 
            if (cur.unlocked) return acc + cur.value;
            if (cur.isFirst || cur.previousIsUnlocked) {
                const goalValue = cur.goalObject.goal;
                const progressRatio = currentValue / goalValue;
                
                if (!currentValue) return goalValue / 10;
                if (progressRatio > 0.85) return goalValue * 0.85;
                return currentValue;
            }
            return acc;
        }, 0) / goals.length);
    }
}